<template>
  <div class="w-full flex flex-col">
    <div class="m-2 p-4 bg-white rounded shadow space-y-4 max-w-5xl">
      <div class="flex items-center gap-3">
        <router-link class="text-sm text-primary underline" to="/Matrices/Courses">Courses</router-link>
        <h1 class="text-lg font-medium text-primary">New training course</h1>
      </div>
      <label class="block space-y-1">
        <span class="text-sm font-medium">Course request</span>
        <textarea class="w-full border rounded p-2 min-h-32" v-model="prompt" placeholder="Audience, objectives, tone, and any requirements."></textarea>
      </label>
      <label class="block space-y-1">
        <span class="text-sm font-medium">Source files</span>
        <input class="block text-sm" type="file" multiple accept=".pdf,.docx,.pptx,.txt,.md,.markdown" @change="onFiles" />
      </label>
      <p class="text-sm text-red-700" v-if="error">{{ error }}</p>
      <ul class="text-sm text-amber-800 list-disc pl-5" v-if="warnings.length">
        <li v-for="warning in warnings" :key="warning">{{ warning }}</li>
      </ul>
      <button class="px-4 py-2 bg-primary text-white rounded disabled:bg-gray-300" type="button" :disabled="loading || prompt.trim().length < 10" @click="generate">
        {{ loading ? "Generating…" : "Generate course" }}
      </button>
      <ProcedureCheckPanel :check="procedureCheck" />
      <p class="text-sm text-red-700" v-if="schemaErrors.length">{{ schemaErrors[0] }}</p>
      <div class="flex gap-2" v-if="schema">
        <button class="px-4 py-2 bg-primary text-white rounded disabled:bg-gray-300" type="button" :disabled="!savable || saving" @click="save">
          {{ saving ? "Saving…" : "Save draft" }}
        </button>
      </div>
    </div>
    <CoursePlayer v-if="schema" :schema="schema" preview />
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { db } from "../../../db.js";
import { validateCourseSchema } from "../../../../functions/functions-common/agents/agents/generateTrainingCourse/schema.mjs";
import CoursePlayer from "./CoursePlayer.vue";
import ProcedureCheckPanel from "./ProcedureCheckPanel.vue";
import { canSaveCourse, generateCourse, readFileAsBase64, saveNewCourse } from "./courseApi.js";

const router = useRouter();
const store = useStore();
const prompt = ref("");
const files = ref([]);
const schema = ref(null);
const procedureCheck = ref(null);
const warnings = ref([]);
const error = ref("");
const loading = ref(false);
const saving = ref(false);

const schemaResult = computed(() => schema.value ? validateCourseSchema(schema.value) : null);
const schemaErrors = computed(() => schemaResult.value?.errors || []);
const savable = computed(() => canSaveCourse(schemaResult.value, procedureCheck.value));

function onFiles(event) {
  files.value = Array.from(event.target.files || []);
}

async function generate() {
  error.value = "";
  loading.value = true;
  try {
    const localWarnings = [];
    const encoded = [];
    for (const file of files.value) {
      if (file.size > 1000000) {
        localWarnings.push(`${file.name} is too large and was skipped.`);
        continue;
      }
      encoded.push(await readFileAsBase64(file));
    }
    const result = await generateCourse({
      mode: "generate",
      prompt: prompt.value.trim(),
      files: encoded,
    });
    schema.value = result.schema;
    procedureCheck.value = result.procedureCheck;
    warnings.value = [...localWarnings, ...(result.warnings || [])];
  } catch (err) {
    error.value = err?.message || "The course could not be generated.";
  } finally {
    loading.value = false;
  }
}

async function save() {
  if (!savable.value) return;
  saving.value = true;
  error.value = "";
  try {
    const id = await saveNewCourse(db, {
      schema: schemaResult.value.schema,
      prompt: prompt.value.trim(),
      procedureCheck: procedureCheck.value,
      userId: store.getters.userID,
    });
    router.push(`/Matrices/Courses/${id}`);
  } catch (err) {
    error.value = err?.message || "The course could not be saved.";
  } finally {
    saving.value = false;
  }
}
</script>
